import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';

import { UserContext } from '../user/User.context';

export interface LoginContextProps {
  previousRoute: string;
  didLogout: boolean;
  setDidLogout: (didLogout: boolean) => void;
}

export const LoginContext = createContext<LoginContextProps>({
  previousRoute: '',
  didLogout: false,
  setDidLogout: () => {}
});

export function useLoginRedirect() {
  const { user, didSessionExpire } = useContext(UserContext);
  const { previousRoute, didLogout } = useContext(LoginContext);
  const { pathname, search, hash } = useLocation();
  const route: string = pathname + search + hash;

  const redirectFromLogin: string | undefined = useMemo(() => {
    if (!(user && user.roles)) {
      return undefined;
    }
    if (user.roles.length === 0) {
      return '/portal/dashboard';
    } else if (user.roles.find(r => r.name === 'Signup')) {
      return '/onboarding';
    } else if (user.roles.find(r => r.name === 'Admin')) {
      return '/portal/dashboard'; // '/portal/admin-dashboard'
    } else if (user.roles.find(r => r.name === 'Trainee')) {
      return '/portal/training';
    } else if (user.roles.find(r => r.name === 'DocumentAdmin')) {
      return '/portal/documents';
    } else if (user.roles.find(r => r.name === 'DocumentViewer')) {
      return '/portal/documents';
    } else if (user.roles.find(r => r.name === 'DocumentEditor')) {
      return '/portal/documents';
    } else if (user.roles.find(r => r.name === 'SiteAdmin')) {
      return '/portal/users';
    } else if (user.roles.find(r => r.name === 'TrainingAdmin')) {
      return '/portal/admin/training';
    } else if (user.roles.find(r => r.name === 'RiskAdmin')) {
      return '/portal/risk';
    } else if (user.roles.find(r => r.name === 'ProductAdmin')) {
      return '/portal/products';
    } else if (user.roles.find(r => r.name === 'HazardAdmin')) {
      return '/portal/hazards';
    } else if (user.roles.find(r => r.name === 'CapaAdmin')) {
      return '/portal/capa';
    } else if (user.roles.find(r => r.name === 'ScarAdmin')) {
      return '/portal/scar';
    } else if (user.roles.find(r => r.name === 'ChangeOrderAdmin')) {
      return '/portal/change-order';
    } else if (user.roles.find(r => r.name === 'SupplierAdmin')) {
      return '/portal/suppliers';
    } else if (user.roles.find(r => r.name === 'WorkflowAdmin')) {
      return '/portal/workflow';
    } else if (user.roles.length > 0) {
      // if user has roles but they are not from default roles,
      // custom roles are assigned and redirect to dashboard
      return '/portal/dashboard';
    }
  }, [user]);

  const loginRedirect: string | undefined = useMemo(() => {
    const params: URLSearchParams = new URLSearchParams(search);
    const to: string | null = params.get('to');
    return to || redirectFromLogin;
  }, [search, redirectFromLogin]);

  const getLoginRoute = useCallback((): string => {
    // if user is logged in, redirect to valid route instead of /login
    if (redirectFromLogin) {
      return redirectFromLogin;
    }
    let paramsStr: string = '';
    // remove redirect if user logs out
    // avoid infinite redirects if route is the same as previous
    if (!didLogout && route !== previousRoute) {
      const params: URLSearchParams = new URLSearchParams();
      params.set('to', route);
      paramsStr = params.toString();
    }
    return `/login${paramsStr && `?${paramsStr}`}`;
  }, [route, didLogout, previousRoute, redirectFromLogin]);

  return { loginRedirect, getLoginRoute, didSessionExpire };
}

export const LoginProvider: React.FC = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const previousRouteRef = useRef<string>('');
  const [didLogout, setDidLogout] = useState<boolean>(false);
  const [previousRoute, setPreviousRoute] = useState<string>('');
  const route: string = pathname + search + hash;

  useEffect(() => {
    setPreviousRoute(previousRouteRef.current);
    previousRouteRef.current = route;
  }, [route]);

  return (
    <LoginContext.Provider value={{ previousRoute, didLogout, setDidLogout }}>
      {children}
    </LoginContext.Provider>
  );
};
